import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  getMigrationJob,
  isMigrationJobInProgress,
  startMigrationImport,
  startMigrationValidate,
  uploadMigrationFile,
  type MigrationImportOptions,
} from '@/lib/api/migration.js';
import { listInventoryBulkEdits, undoInventoryBulkEdit } from '@/lib/api/inventory.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';

type PreviewChange = { field: string; before: string; after: string };
type PreviewRow = {
  rowNumber: number;
  action: string;
  name?: string;
  sku?: string;
  error?: string | null;
  warnings?: string[];
  changes?: PreviewChange[];
  guardedIgnored?: string[];
};

export function InventoryBulkPage() {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [allowTrackInventory, setAllowTrackInventory] = useState(false);
  const [allowCondition, setAllowCondition] = useState(false);
  const [allowArchived, setAllowArchived] = useState(false);
  const [createMissingDevices, setCreateMissingDevices] = useState(false);
  const [createMissingSuppliers, setCreateMissingSuppliers] = useState(false);

  const options: MigrationImportOptions = {
    allowTrackInventory,
    allowCondition,
    allowArchived,
    createMissingDevices,
    createMissingSuppliers,
  };

  const jobQuery = useQuery({
    queryKey: ['inventory-bulk-job', jobId],
    queryFn: () => getMigrationJob(jobId!),
    enabled: Boolean(jobId),
    refetchInterval: (query) =>
      isMigrationJobInProgress(query.state.data?.status ?? '') ? 1500 : false,
  });
  const batchesQuery = useQuery({
    queryKey: ['inventory-bulk-edits'],
    queryFn: listInventoryBulkEdits,
  });

  const validateMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Choose a file');
      const uploaded = await uploadMigrationFile(file, 'inventory_bulk');
      await startMigrationValidate(uploaded.jobId, options);
      return uploaded.jobId;
    },
    onSuccess: (id) => {
      setJobId(id);
      toastSuccess('Checking the file');
    },
    onError: (err: unknown) => toastError(parseApiError(err).message),
  });

  const importMutation = useMutation({
    mutationFn: async () => {
      if (!jobId) throw new Error('Nothing to apply');
      return startMigrationImport(jobId, options);
    },
    onSuccess: () => toastSuccess('Applying the file'),
    onError: (err: unknown) => toastError(parseApiError(err).message),
  });

  const undoMutation = useMutation({
    mutationFn: undoInventoryBulkEdit,
    onSuccess: async () => {
      toastSuccess('Bulk edit undone');
      await queryClient.invalidateQueries({ queryKey: ['inventory-bulk-edits'] });
      await queryClient.invalidateQueries({ queryKey: ['inventory-items'] });
    },
    onError: (err: unknown) => toastError(parseApiError(err).message),
  });

  const job = jobQuery.data;
  const preview = (job?.validationResult?.preview ?? []) as PreviewRow[];
  const summaryLines = job?.validationResult?.warnings ?? [];
  const busy = isMigrationJobInProgress(job?.status ?? '') || validateMutation.isPending || importMutation.isPending;

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <div>
        <Link to="/inventory" className="text-sm font-semibold text-[#1B4F8A]">
          Back to inventory
        </Link>
        <h1 className="mt-2 text-2xl font-semibold text-slate-900">Import inventory edits</h1>
        <p className="mt-1 text-sm text-slate-600">
          Upload a spreadsheet exported from the inventory list. Nothing is written until you confirm the preview.
          Rows left out of the file are not changed.
        </p>
      </div>

      <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5">
        <label className="block text-sm font-semibold text-slate-700">
          Spreadsheet
          <input
            type="file"
            accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
            className="mt-2 block w-full text-sm"
            onChange={(event) => {
              setFile(event.target.files?.[0] ?? null);
              setJobId(null);
            }}
          />
        </label>

        <div className="grid gap-2 text-sm text-slate-700">
          <label className="flex items-start gap-2">
            <input type="checkbox" checked={allowTrackInventory} onChange={(e) => { setAllowTrackInventory(e.target.checked); setJobId(null); }} />
            <span>Apply track_inventory changes. Turning tracking off leaves existing stock layers behind.</span>
          </label>
          <label className="flex items-start gap-2">
            <input type="checkbox" checked={allowCondition} onChange={(e) => { setAllowCondition(e.target.checked); setJobId(null); }} />
            <span>Apply condition changes.</span>
          </label>
          <label className="flex items-start gap-2">
            <input type="checkbox" checked={allowArchived} onChange={(e) => { setAllowArchived(e.target.checked); setJobId(null); }} />
            <span>Apply archived changes.</span>
          </label>
          <label className="flex items-start gap-2">
            <input type="checkbox" checked={createMissingDevices} onChange={(e) => { setCreateMissingDevices(e.target.checked); setJobId(null); }} />
            <span>Create missing device makes and models for compatible_devices.</span>
          </label>
          <label className="flex items-start gap-2">
            <input type="checkbox" checked={createMissingSuppliers} onChange={(e) => { setCreateMissingSuppliers(e.target.checked); setJobId(null); }} />
            <span>Create missing suppliers named in supplier_skus.</span>
          </label>
        </div>

        <button
          type="button"
          disabled={!file || busy}
          className="rounded-lg bg-[#1B4F8A] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          onClick={() => validateMutation.mutate()}
        >
          Preview changes
        </button>
      </section>

      {job ? (
        <section className="space-y-3 rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-slate-900">Preview</h2>
            <span className="text-sm text-slate-500">{job.status}</span>
          </div>
          {job.errorMessage ? <p className="text-sm text-red-700">{job.errorMessage}</p> : null}
          <ul className="space-y-1 text-sm text-slate-800">
            {summaryLines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          <div className="max-h-[28rem] space-y-2 overflow-auto">
            {preview.map((row) => (
              <details key={`${row.rowNumber}-${row.sku ?? ''}`} className="rounded-lg border border-slate-200 px-3 py-2">
                <summary className="cursor-pointer text-sm font-semibold text-slate-800">
                  Row {row.rowNumber}: {row.action} {row.name || row.sku || ''}
                </summary>
                <div className="mt-2 space-y-1 text-sm text-slate-600">
                  {row.error ? <p className="text-red-700">{row.error}</p> : null}
                  {(row.warnings ?? []).map((warning) => (
                    <p key={warning}>{warning}</p>
                  ))}
                  {(row.guardedIgnored ?? []).length > 0 ? (
                    <p>
                      In the file but not applied because the opt-in is off: {(row.guardedIgnored ?? []).join(', ')}
                    </p>
                  ) : null}
                  <ul>
                    {(row.changes ?? []).map((change) => (
                      <li key={change.field}>
                        {change.field}: {change.before || '—'} → {change.after || '—'}
                      </li>
                    ))}
                  </ul>
                </div>
              </details>
            ))}
          </div>
          <button
            type="button"
            disabled={job.status !== 'validated' || busy}
            className="rounded-lg bg-[#E8793A] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            onClick={() => importMutation.mutate()}
          >
            Apply changes
          </button>
          {job.status === 'completed' ? (
            <p className="text-sm text-slate-600">
              Applied {job.importedCount} row(s). Skipped {job.skippedCount}.
            </p>
          ) : null}
        </section>
      ) : null}

      <section className="space-y-3 rounded-2xl border border-slate-200 bg-white p-5">
        <h2 className="text-lg font-semibold text-slate-900">Undo</h2>
        <p className="text-sm text-slate-600">
          Undo restores field values, reverses stock, and removes items this import created. It stops if any of those
          items had stock movement after the import.
        </p>
        <ul className="divide-y divide-slate-100 text-sm">
          {(batchesQuery.data ?? []).map((batch) => (
            <li key={batch.id} className="flex items-center justify-between gap-3 py-2">
              <span>
                {new Date(batch.performedAt).toLocaleString()} · {batch.itemCount} items
                {batch.reversedAt ? ' · undone' : ''}
              </span>
              {batch.reversedAt ? null : (
                <button
                  type="button"
                  className="rounded-lg border border-slate-300 px-3 py-1 font-semibold"
                  disabled={undoMutation.isPending}
                  onClick={() => undoMutation.mutate(batch.id)}
                >
                  Undo
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
